import { logger } from "@/lib/logger";
import Message, {
  MessageRating,
  TEMP_AI_MESSAGE_ID,
} from "@/models/conversation/Message";
import {
  createInFlightStreamState,
  type InFlightStreamState,
  isStreamContext,
  type StreamAccumulatorState,
  type StreamContext,
} from "@/lib/inFlightStreamState";

const toMessageLemmatizedText = (
  lemmatizedText: Record<string, string>
): Message["lemmatizedText"] =>
  Object.entries(lemmatizedText).map(([word, lemmatizedWord]) => ({
    word,
    lemmatizedWord,
  }));

export const IN_FLIGHT_STREAMS_STORAGE_KEY = "chat:inFlightStreams";

export const inFlightStreamsStorageKey = (userId: string): string =>
  `${IN_FLIGHT_STREAMS_STORAGE_KEY}:${userId}`;

/** Match chat-api `STREAM_GENERATION_RETENTION_DAYS` (90 days). */
export const IN_FLIGHT_STREAM_TTL_MS = 90 * 24 * 60 * 60 * 1000;

export type PersistedInFlightStream = {
  conversationId: string;
  generationId: string;
  lastChunkSeq: number;
  streamHandoffDone: boolean;
  waitingForResponse: boolean;
  isTerminal: boolean;
  latestMessage: Message | null;
  streamState: StreamAccumulatorState;
  streamText: string;
  referenceReplacements: Record<string, string>;
  inFlightHumanMessageId: string | null;
  inFlightHumanMessagePersisted: boolean;
  streamContext: StreamContext | null;
  persistedAt: number;
};

type PersistedInFlightStreamMap = Record<string, PersistedInFlightStream>;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isStringRecord = (value: unknown): value is Record<string, string> => {
  if (!isRecord(value)) return false;
  return Object.values(value).every((entry) => typeof entry === "string");
};

const isMessageRating = (value: unknown): value is MessageRating =>
  value === MessageRating.DownVote ||
  value === MessageRating.NeutralVote ||
  value === MessageRating.UpVote;

const isStreamAccumulatorState = (
  value: unknown
): value is StreamAccumulatorState => {
  if (!isRecord(value)) return false;
  return (
    typeof value.text === "string" &&
    typeof value.chainOfThoughtMessage === "string" &&
    typeof value.revisedPrompt === "string" &&
    typeof value.statusShort === "string" &&
    typeof value.statusLong === "string" &&
    Array.isArray(value.references) &&
    isStringRecord(value.lemmatizedText)
  );
};

const isMessage = (value: unknown): value is Message => {
  if (!isRecord(value)) return false;
  return (
    typeof value.id === "string" &&
    typeof value.conversationId === "string" &&
    typeof value.human === "boolean" &&
    typeof value.text === "string" &&
    isMessageRating(value.rating) &&
    Array.isArray(value.references) &&
    typeof value.createdAt === "string" &&
    typeof value.updatedAt === "string"
  );
};

const isPersistedInFlightStream = (
  value: unknown
): value is PersistedInFlightStream => {
  if (!isRecord(value)) return false;
  return (
    typeof value.conversationId === "string" &&
    typeof value.generationId === "string" &&
    typeof value.lastChunkSeq === "number" &&
    typeof value.streamHandoffDone === "boolean" &&
    typeof value.waitingForResponse === "boolean" &&
    typeof value.isTerminal === "boolean" &&
    (value.latestMessage === null || isMessage(value.latestMessage)) &&
    isStreamAccumulatorState(value.streamState) &&
    typeof value.streamText === "string" &&
    isStringRecord(value.referenceReplacements) &&
    (value.inFlightHumanMessageId === null ||
      typeof value.inFlightHumanMessageId === "string") &&
    typeof value.inFlightHumanMessagePersisted === "boolean" &&
    (value.streamContext === null || isStreamContext(value.streamContext)) &&
    typeof value.persistedAt === "number"
  );
};

const readPersistedMap = (userId: string): PersistedInFlightStreamMap => {
  if (typeof localStorage === "undefined" || !userId) {
    return {};
  }

  const raw = localStorage.getItem(inFlightStreamsStorageKey(userId));
  if (!raw) return {};

  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed)) return {};

    const result: PersistedInFlightStreamMap = {};
    for (const [conversationId, entry] of Object.entries(parsed)) {
      if (isPersistedInFlightStream(entry)) {
        result[conversationId] = entry;
      }
    }
    return result;
  } catch {
    return {};
  }
};

const writePersistedMap = (
  userId: string,
  map: PersistedInFlightStreamMap
): void => {
  if (typeof localStorage === "undefined" || !userId) {
    return;
  }

  const storageKey = inFlightStreamsStorageKey(userId);
  try {
    if (Object.keys(map).length === 0) {
      localStorage.removeItem(storageKey);
      return;
    }

    localStorage.setItem(storageKey, JSON.stringify(map));
  } catch (error) {
    logger.warn("Failed to persist in-flight stream state to localStorage", {
      operation: "writePersistedMap",
      storageKey,
      errorMessage: error instanceof Error ? error.message : String(error),
    });
  }
};

export const toPersistedInFlightStream = (
  state: InFlightStreamState
): PersistedInFlightStream | null => {
  if (state.isTerminal || !state.waitingForResponse || !state.generationId) {
    return null;
  }

  return {
    conversationId: state.conversationId,
    generationId: state.generationId,
    lastChunkSeq: state.lastChunkSeq,
    streamHandoffDone: state.streamHandoffDone,
    waitingForResponse: state.waitingForResponse,
    isTerminal: state.isTerminal,
    latestMessage: state.latestMessage,
    streamState: state.streamState,
    streamText: state.streamText,
    referenceReplacements: state.referenceReplacements,
    inFlightHumanMessageId: state.inFlightHumanMessageId,
    inFlightHumanMessagePersisted: state.inFlightHumanMessagePersisted,
    streamContext: state.streamContext,
    persistedAt: Date.now(),
  };
};

export const fromPersistedInFlightStream = (
  persisted: PersistedInFlightStream
): InFlightStreamState => {
  const textLength = persisted.streamState.text.length;
  const cotLength = persisted.streamState.chainOfThoughtMessage.length;
  const revisedLength = persisted.streamState.revisedPrompt.length;
  const statusLongLength = persisted.streamState.statusLong.length;

  return {
    ...createInFlightStreamState(
      persisted.conversationId,
      persisted.generationId
    ),
    lastChunkSeq: persisted.lastChunkSeq,
    streamHandoffDone: persisted.streamHandoffDone,
    waitingForResponse: persisted.waitingForResponse,
    isTerminal: persisted.isTerminal,
    latestMessage: persisted.latestMessage,
    streamState: { ...persisted.streamState },
    streamText: persisted.streamText,
    referenceReplacements: { ...persisted.referenceReplacements },
    typingBuffer: "",
    cotBuffer: "",
    revisedPromptBuffer: "",
    statusLongBuffer: "",
    queuedCharCount: textLength,
    queuedCotCount: cotLength,
    queuedRevisedPromptCount: revisedLength,
    queuedStatusLongCount: statusLongLength,
    pendingClosedMessage: null,
    inFlightHumanMessageId: persisted.inFlightHumanMessageId,
    inFlightHumanMessagePersisted: persisted.inFlightHumanMessagePersisted,
    streamContext: persisted.streamContext,
  };
};

export const synthesizeLatestMessageFromStreamState = (
  state: InFlightStreamState
): Message | null => {
  if (state.latestMessage) {
    return {
      ...state.latestMessage,
      text: state.streamState.text,
      chainOfThoughtMessage: state.streamState.chainOfThoughtMessage,
      revisedPrompt: state.streamState.revisedPrompt,
      statusShort: state.streamState.statusShort,
      statusLong: state.streamState.statusLong,
      references: state.streamState.references,
      lemmatizedText: toMessageLemmatizedText(state.streamState.lemmatizedText),
      isClosed: false,
    };
  }

  const hasContent =
    state.streamState.text.length > 0 ||
    state.streamState.chainOfThoughtMessage.length > 0 ||
    state.streamState.statusShort.length > 0 ||
    state.streamState.statusLong.length > 0;

  if (!hasContent) {
    return null;
  }

  const languageCode = state.streamContext?.languageCode ?? "da";

  return {
    id: TEMP_AI_MESSAGE_ID,
    conversationId: state.conversationId,
    human: false,
    text: state.streamState.text,
    chainOfThoughtMessage: state.streamState.chainOfThoughtMessage,
    revisedPrompt: state.streamState.revisedPrompt,
    statusShort: state.streamState.statusShort,
    statusLong: state.streamState.statusLong,
    references: state.streamState.references,
    lemmatizedText: toMessageLemmatizedText(state.streamState.lemmatizedText),
    language: languageCode,
    isClosed: false,
    rating: MessageRating.NeutralVote,
    createdAt: "",
    updatedAt: "",
    chainOfThought: false,
  };
};

export const evictExpiredPersistedStreams = (
  userId: string,
  ttlMs: number = IN_FLIGHT_STREAM_TTL_MS
): void => {
  if (!userId) {
    return;
  }

  const map = readPersistedMap(userId);
  const now = Date.now();
  let changed = false;

  for (const [conversationId, entry] of Object.entries(map)) {
    if (now - entry.persistedAt > ttlMs) {
      delete map[conversationId];
      changed = true;
    }
  }

  if (changed) {
    writePersistedMap(userId, map);
  }
};

export const persistInFlightStream = (
  userId: string | null | undefined,
  state: InFlightStreamState
): void => {
  if (!userId) {
    return;
  }

  const persisted = toPersistedInFlightStream(state);
  if (!persisted) {
    clearPersistedInFlightStream(userId, state.conversationId);
    return;
  }

  const map = readPersistedMap(userId);
  map[state.conversationId] = persisted;
  writePersistedMap(userId, map);
};

export const persistInFlightStreamMap = (
  userId: string | null | undefined,
  streams: Map<string, InFlightStreamState>
): void => {
  if (!userId) {
    return;
  }

  const map = readPersistedMap(userId);

  for (const [conversationId, state] of streams) {
    const persisted = toPersistedInFlightStream(state);
    if (persisted) {
      map[conversationId] = persisted;
    } else {
      delete map[conversationId];
    }
  }

  writePersistedMap(userId, map);
};

export const getPersistedInFlightStream = (
  userId: string | null | undefined,
  conversationId: string
): InFlightStreamState | null => {
  if (!userId) {
    return null;
  }

  evictExpiredPersistedStreams(userId);

  const entry = readPersistedMap(userId)[conversationId];
  if (!entry || entry.isTerminal || !entry.waitingForResponse) {
    return null;
  }

  return fromPersistedInFlightStream(entry);
};

export const hydrateInFlightStreamsFromStorage = (
  userId: string | null | undefined
): Map<string, InFlightStreamState> => {
  if (!userId) {
    return new Map();
  }

  evictExpiredPersistedStreams(userId);

  const map = readPersistedMap(userId);
  const result = new Map<string, InFlightStreamState>();

  for (const [conversationId, entry] of Object.entries(map)) {
    if (!entry.isTerminal && entry.waitingForResponse) {
      result.set(conversationId, fromPersistedInFlightStream(entry));
    }
  }

  return result;
};

export const clearPersistedInFlightStream = (
  userId: string | null | undefined,
  conversationId: string
): void => {
  if (!userId) {
    return;
  }

  const map = readPersistedMap(userId);
  if (!(conversationId in map)) {
    return;
  }

  delete map[conversationId];
  writePersistedMap(userId, map);
};

export const clearAllPersistedInFlightStreams = (
  userId: string | null | undefined
): void => {
  if (typeof localStorage === "undefined" || !userId) {
    return;
  }
  localStorage.removeItem(inFlightStreamsStorageKey(userId));
};
